import { cx } from '@linaria/core';
import { useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { appContext, stringifyPublisher } from '@provider/app-context';
import { useNotifier } from '@provider/notifier';
import { userIdentityStore } from '@provider/user-identity';
import { Tooltip } from '@ui/kit';
import { LogoutIcon, KeyIcon } from '@ui/kit/icons';
import { loadExportLibrary, slugify, asyncTimeout } from '@utils/common';
import { CSSRefTransition } from '@utils/react';
import { ModalButton } from '../atom.modal-button.js';
import { useProgress } from '../hook.progress.js';
import { buttonWrapper, authButtonWrapper, authButton, authButtonTransitionDurationMs, email, cta, authButtonIconWrapper, authButtonIcon, spinner } from './atom.drive-export-button.style.js';
import { useSaveCloudEntryMutation } from './mutation.save-cloud-entry.js';
import { useGetCloudEntriesQuery } from './query.get-cloud-entries.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useComponentEntry } from '../../../api.component-entry/query.get.js';

function DriveExportButton(props) {
  const notifier = useNotifier();
  const {
    data: componentEntryId
  } = useComponentEntry(props.entryKey, {
    select: entry => entry?.envelope.objectID
  });
  const {
    data: modifiedAt
  } = useComponentEntry(props.entryKey, {
    select: entry => entry?.envelope.modified ? new Date(entry.envelope.modified * 1000) : undefined
  });
  const saveCloundEntryMutation = useSaveCloudEntryMutation(componentEntryId);
  const cloudEntries = useGetCloudEntriesQuery(componentEntryId);
  const [isAuthorized, setAuthorized] = useState(props.api?.isAuthorized() ?? false);
  const [isAuthorizing, setAuthorizing] = useState(false);
  const [userMail, setUserMail] = useState(undefined);
  const [progress, setProgress, resetProgress] = useProgress();
  const hasUploadedMostRecentEntry = (() => {
    if (!modifiedAt || !props.cloudEntry || !isAuthorized) {
      return false;
    }
    return modifiedAt <= props.cloudEntry.modifiedAt;
  })();

  // auto-fetch user's e-mail-adress if user has been initially authorized, but
  // we haven't fetched his mail-address yet
  useEffect(() => {
    if (props.api === undefined) {
      return;
    }
    setAuthorizing(true);
    props.api.onInitialAuthorization(() => {
      if (props.api?.isAuthorized()) {
        props.api.getUserData().then(userData => {
          setAuthorizing(false);
          setAuthorized(true);
          setUserMail(userData.mail);
        }, err => {
          setAuthorizing(false);
          console.error(err);
        });
      } else {
        setAuthorizing(false);
      }
    });
  }, [props.api]);
  const handleLogin = () => {
    if (props.api === undefined) {
      notifier.notify({
        id: "drive-export.login.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at logge dig ind på dit drev."
      });
      return;
    }
    if (isAuthorizing) {
      return;
    }
    setAuthorizing(true);
    props.api.authorize().then(() => props.api?.getUserData().then(userData => {
      setUserMail(userData.mail);
      setAuthorizing(false);
      setAuthorized(true);
    })).catch(reason => {
      notifier.notify({
        id: "drive-export.login.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at logge dig ind på dit drev."
      });
      console.error(reason);
      setAuthorizing(false);
    });
  };
  const handleLogout = () => {
    if (props.api === undefined) {
      notifier.notify({
        id: "drive-export.logout.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at logge dig ud af dit drev."
      });
      return;
    }
    if (isAuthorizing) {
      return;
    }
    setAuthorizing(false);
    props.api.signOut().then(() => {
      setUserMail(undefined);
      setAuthorizing(false);
      setAuthorized(false);
    }).catch(reason => {
      notifier.notify({
        id: "drive-export.logout.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at logge dig ud af dit drev."
      });
      console.error(reason);
      setAuthorizing(false);
    });
  };
  const handleSave = async () => {
    const gExport = window.gExport ?? (await loadExportLibrary().catch(err => {
      console.warn(err);
    }));
    if (props.api === undefined || !isAuthorized || cloudEntries.isLoading || !gExport) {
      notifier.notify({
        id: "drive-export.save.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at gemme din besvarelse på dit drev."
      });
      return;
    }
    setProgress(0);
    let fileConfigPromise = Promise.resolve({
      fileName: slugify(props.componentTitle),
      folderId: "root",
      fileId: props.cloudEntry?.fileId
    });

    // if we're overwriting existing files, we need to do some custom lookup on
    // the files we're working on, in order to ensure that the expected results
    // occur - regardless of remote drive status
    if (props.cloudEntry !== undefined) {
      const version = await notifier.dialog({
        heading: "Du har allerede en besvarelse i dit drev",
        body: "Vil du gemme besvarelsen som ny, eller vil du gemme dine ændringer i den eksisterende besvarelse?",
        actions: [{
          intent: "new",
          label: "Gem som ny",
          type: "secondary"
        }, {
          intent: "overwrite",
          label: "Gem ændringer",
          type: "primary"
        }]
      });
      if (!version) {
        resetProgress();
        return;
      }
      fileConfigPromise = new Promise(resolve => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        props.api.getFileData(props.cloudEntry.fileId, res => {
          // assume that the user deleted the file, in case something went wrong
          // and we couldn't locate the existing file
          if (res === undefined || "error" in res || res.explicitlyTrashed === true) {
            resolve({
              fileName: slugify(props.componentTitle),
              folderId: "root",
              fileId: version === "new" ? undefined : props.cloudEntry?.fileId
            });
            return;
          }

          // specifically for OneDrive, we'll need to overwrite fileName and
          // folderId to ensure that the file is properly overwritten due to
          // unfortunate inconsistencies in the apis between the two Drives
          if (props.api?.getFileData === window.gExport?.MicrosoftApi.getFileData) {
            resolve({
              fileName: res.fileName,
              folderId: res.folderId,
              fileId: version === "new" ? undefined : props.cloudEntry?.fileId
            });
          } else {
            // ... otherwise just allow overwriting the existing file
            resolve({
              fileName: slugify(props.componentTitle),
              folderId: "root",
              fileId: version === "new" ? undefined : props.cloudEntry?.fileId
            });
          }
        });
      });
    }
    const fileConfig = await fileConfigPromise;
    await props.onBeforeExport?.();
    const input = await props.getInput(props.format);
    const context = appContext.getState();
    const user = userIdentityStore.getState().user;
    const uploadArgs = await (async () => {
      switch (props.format) {
        case "pdf":
          return {
            ...fileConfig,
            input,
            format: props.format,
            preset: `full-paper.${props.printOrientation}`,
            baseUrl: window.location.href
          };
        case "docx":
          return {
            ...fileConfig,
            input,
            format: props.format,
            baseUrl: window.location.href,
            templateData: {
              product: context.page,
              course: context.course ? {
                title: context.course?.title,
                authors: (await appContext.getPageAuthors())?.join(", ")
              } : undefined,
              date: new Date().toLocaleDateString("da-DK", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric"
              }),
              publisher: stringifyPublisher(context.publisher),
              user: user ? {
                name: user?.name,
                institution: user?.institution?.name
              } : undefined
            }
          };
      }
    })();
    let hasTriggeredHTMLSentEvent = false;
    props.api?.upload(uploadArgs).then(async response => {
      resetProgress();
      const cloudEntry = {
        fileId: response.data.id,
        modifiedAt: modifiedAt ?? new Date()
      };
      const data = props.type === "googleDrive" ? {
        googleDrive: cloudEntry
      } : {
        oneDrive: cloudEntry
      };
      saveCloundEntryMutation.mutate(data);
      notifier.notify({
        id: "drive-export.save",
        type: "success",
        body: "Din besvarelse er blevt gemt på dit drev."
      });
      await props.onAfterExport?.();
    }, async reason => {
      console.error(reason);
      resetProgress();
      notifier.notify({
        id: "drive-export.save.error",
        type: "error",
        body: "Der opstod en fejl, da vi forsøgte at gemme din besvarelse på dit drev."
      });
      if (!hasTriggeredHTMLSentEvent) {
        hasTriggeredHTMLSentEvent = true;
        await asyncTimeout(10);
        await props.onAfterHTMLSent?.();
      }
      await props.onAfterExport?.();
    }, async evt => {
      setProgress(evt.progress / 100);
      if (evt.progress > 0 && !hasTriggeredHTMLSentEvent) {
        hasTriggeredHTMLSentEvent = true;
        await asyncTimeout(10);
        await props.onAfterHTMLSent?.();
      }
    });
  };
  return /*#__PURE__*/jsxs("div", {
    className: buttonWrapper,
    children: [/*#__PURE__*/jsx(ModalButton, {
      onClick: handleSave,
      disabled: isAuthorizing || !isAuthorized || cloudEntries.isLoading || !!progress,
      async: {
        type: "upload",
        progress
      },
      state: hasUploadedMostRecentEntry ? "success" : undefined,
      children: props.children
    }), /*#__PURE__*/jsx(TransitionGroup, {
      className: authButtonWrapper,
      children: /*#__PURE__*/jsx(CSSRefTransition, {
        timeout: authButtonTransitionDurationMs,
        classNames: authButton,
        children: ref => /*#__PURE__*/jsx(Tooltip, {
          containerRef: ref,
          placement: "bottom",
          body: `${isAuthorized ? "Log ud af" : "Log ind på"} ${props.type === "googleDrive" ? "Google Drev" : "OneDrive"}`,
          ariaType: "description",
          children: ref => /*#__PURE__*/jsxs("button", {
            ref: ref,
            className: cx(authButton, typeof progress !== "undefined" && "hidden"),
            onClick: isAuthorized ? handleLogout : handleLogin,
            children: [userMail && /*#__PURE__*/jsx("span", {
              className: email,
              children: userMail
            }), /*#__PURE__*/jsxs("span", {
              className: cta,
              children: [/*#__PURE__*/jsx("span", {
                children: isAuthorized ? "Log ud" : "Log ind"
              }), /*#__PURE__*/jsx(TransitionGroup, {
                className: authButtonIconWrapper,
                children: /*#__PURE__*/jsx(CSSRefTransition, {
                  classNames: authButtonIcon,
                  timeout: authButtonTransitionDurationMs,
                  children: ref => /*#__PURE__*/jsx("span", {
                    className: authButtonIcon,
                    ref: ref,
                    children: isAuthorizing ? /*#__PURE__*/jsx("span", {
                      className: spinner
                    }) : isAuthorized ? /*#__PURE__*/jsx(LogoutIcon, {
                      height: "12"
                    }) : /*#__PURE__*/jsx(KeyIcon, {
                      height: "12"
                    })
                  })
                }, `${isAuthorizing}`)
              })]
            })]
          })
        })
      }, `auth-button-${isAuthorized}`)
    })]
  });
}

export { DriveExportButton };
//# sourceMappingURL=atom.drive-export-button.js.map
